import React, { useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { Eye, EyeOff, Lock, Mail } from 'lucide-react';
import { BRAND } from '../lib/brand';
import { supabase, getRememberDevice, setRememberDevice } from '../lib/supabase';

export default function LoginPage() {
  const { signIn, resetPassword } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(getRememberDevice());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [mode, setMode] = useState('login'); // 'login' | 'reset'
  const [resetSent, setResetSent] = useState(false);

  // A locked-out client has no session, so nothing else in the app can report this
  // on their behalf. Awaited inside its own try/catch rather than chained: rpc()
  // returns a thenable, not a Promise, and this must never disrupt the login screen.
  async function reportFailedSignIn(attemptedEmail) {
    try {
      // read-check: ignore — monitoring only, and nothing on screen depends on it.
      await supabase.rpc('record_login_failure', { p_email: attemptedEmail });
    } catch { /* monitoring only */ }
  }

  async function handleLogin(e) {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      setRememberDevice(rememberMe);
      await signIn(email, password);
    } catch (err) {
      // Only wrong credentials count: a network failure isn't the client being
      // stuck, and flagging it would cry wolf.
      if (err?.code === 'invalid_credentials' || /invalid login credentials/i.test(err?.message || '')) {
        reportFailedSignIn(email);
      }
      setError(err.message || 'Login failed. Please check your details.');
    } finally {
      setLoading(false);
    }
  }

  async function handleReset(e) {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await resetPassword(email);
      setResetSent(true);
    } catch (err) {
      setError(err.message || 'Could not send reset email.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={styles.page}>
      <div style={styles.background} />

      <div style={styles.container}>
        <div style={styles.card}>
          {/* Header */}
          <div style={styles.header}>
            <div style={styles.logoMark}>
              <img src={BRAND.logo} alt={BRAND.name} style={styles.logoImg} />
            </div>
            <h1 style={styles.title}>{BRAND.name}</h1>
            <p style={styles.subtitle}>
              {mode === 'login' ? 'Your personal exercise programme' : 'Reset your password'}
            </p>
          </div>

          {mode === 'login' ? (
            <form onSubmit={handleLogin} style={styles.form}>
              <div className="form-group">
                <label className="form-label" htmlFor="email">Email</label>
                <div style={styles.inputWrapper}>
                  <Mail size={16} style={styles.inputIcon} />
                  <input
                    id="email"
                    type="email"
                    className="form-input"
                    style={styles.iconInput}
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    required
                    autoComplete="email"
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="password">Password</label>
                <div style={styles.inputWrapper}>
                  <Lock size={16} style={styles.inputIcon} />
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    className="form-input"
                    style={{ ...styles.iconInput, paddingRight: '2.75rem' }}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(v => !v)}
                    style={styles.eyeBtn}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              {/* Remember me — localStorage session instead of per-tab */}
              <div style={styles.rememberBox}>
                <label style={styles.rememberLabel}>
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={e => setRememberMe(e.target.checked)}
                    style={styles.checkbox}
                  />
                  <span style={styles.rememberText}>
                    Remember me on this device
                    <span style={styles.rememberHint}>
                      Leave unticked on shared or clinic computers.
                    </span>
                  </span>
                </label>
              </div>

              {error && <div style={styles.error}>{error}</div>}

              <button
                type="submit"
                className="btn btn-primary"
                style={styles.submitBtn}
                disabled={loading}
              >
                {loading ? <span className="spinner" style={{ width: '1rem', height: '1rem', borderWidth: '2px' }} /> : null}
                {loading ? 'Signing in...' : 'Sign in'}
              </button>

              <button
                type="button"
                onClick={() => { setMode('reset'); setError(''); }}
                style={styles.forgotBtn}
              >
                Forgot your password?
              </button>
            </form>
          ) : (
            <form onSubmit={handleReset} style={styles.form}>
              {resetSent ? (
                <div style={styles.successBox}>
                  <p>Check your email. We've sent a password reset link.</p>
                  <button
                    type="button"
                    className="btn btn-outline"
                    style={{ marginTop: '1rem', width: '100%', justifyContent: 'center' }}
                    onClick={() => { setMode('login'); setResetSent(false); }}
                  >
                    Back to sign in
                  </button>
                </div>
              ) : (
                <>
                  <div className="form-group">
                    <label className="form-label" htmlFor="reset-email">Your email address</label>
                    <input
                      id="reset-email"
                      type="email"
                      className="form-input"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      placeholder="your@email.com"
                      required
                    />
                  </div>

                  {error && <div style={styles.error}>{error}</div>}

                  <button
                    type="submit"
                    className="btn btn-primary"
                    style={styles.submitBtn}
                    disabled={loading}
                  >
                    {loading ? 'Sending...' : 'Send reset link'}
                  </button>

                  <button
                    type="button"
                    onClick={() => { setMode('login'); setError(''); }}
                    style={styles.forgotBtn}
                  >
                    Back to sign in
                  </button>
                </>
              )}
            </form>
          )}
        </div>

        <p style={styles.footer}>
          © {new Date().getFullYear()} {BRAND.name} ·{' '}
          <a href={`https://${BRAND.website}`} target="_blank" rel="noreferrer">
            {BRAND.website}
          </a>
        </p>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    padding: '1rem',
    background: 'var(--cream)',
  },
  background: {
    position: 'fixed',
    inset: 0,
    background: `
      radial-gradient(ellipse 60% 50% at 20% 20%, rgba(var(--navy-rgb),0.12) 0%, transparent 60%),
      radial-gradient(ellipse 50% 40% at 80% 80%, rgba(var(--terracotta-rgb),0.1) 0%, transparent 60%)
    `,
    zIndex: 0,
  },
  container: {
    width: '100%',
    maxWidth: '420px',
    position: 'relative',
    zIndex: 1,
  },
  card: {
    background: 'var(--white)',
    borderRadius: 'var(--radius-xl)',
    boxShadow: 'var(--shadow-lg)',
    border: '1px solid rgba(var(--navy-rgb),0.08)',
    overflow: 'hidden',
  },
  header: {
    background: 'var(--navy)',
    padding: '2rem 2rem 1.5rem',
    textAlign: 'center',
  },
  logoMark: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '0.75rem',
  },
  logoImg: {
    width: '72px',
    height: '72px',
    borderRadius: '50%',
    objectFit: 'cover',
    border: '2px solid rgba(var(--terracotta-rgb),0.5)',
  },
  title: {
    color: 'var(--cream)',
    fontFamily: 'var(--font-serif)',
    fontSize: '1.6rem',
    fontWeight: 400,
    margin: 0,
  },
  subtitle: {
    color: 'rgba(var(--cream-rgb),0.7)',
    fontSize: '0.875rem',
    marginTop: '0.4rem',
  },
  form: {
    padding: '1.75rem 1.75rem 1.25rem',
  },
  inputWrapper: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  },
  inputIcon: {
    position: 'absolute',
    left: '0.75rem',
    color: 'var(--navy)',
    opacity: 0.5,
    pointerEvents: 'none',
  },
  iconInput: {
    paddingLeft: '2.4rem',
  },
  eyeBtn: {
    position: 'absolute',
    right: '0.75rem',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: 'var(--navy)',
    opacity: 0.5,
    display: 'flex',
    alignItems: 'center',
    padding: '0.25rem',
  },
  rememberBox: {
    background: 'var(--cream)',
    borderRadius: 'var(--radius-md)',
    padding: '0.85rem 1rem',
    marginBottom: '1.25rem',
  },
  rememberLabel: {
    display: 'flex',
    gap: '0.75rem',
    alignItems: 'flex-start',
    cursor: 'pointer',
  },
  checkbox: {
    marginTop: '2px',
    width: '18px',
    height: '18px',
    flexShrink: 0,
    accentColor: 'var(--navy)',
  },
  rememberText: {
    fontSize: '0.9rem',
    fontWeight: 500,
    color: 'var(--charcoal)',
    lineHeight: 1.5,
  },
  rememberHint: {
    display: 'block',
    fontSize: '0.78rem',
    fontWeight: 400,
    opacity: 0.65,
    marginTop: '0.15rem',
  },
  error: {
    background: 'var(--danger-bg)',
    color: 'var(--danger)',
    borderRadius: 'var(--radius-sm)',
    padding: '0.65rem 0.9rem',
    fontSize: '0.875rem',
    marginBottom: '1rem',
  },
  successBox: {
    background: 'var(--success-bg)',
    color: 'var(--success)',
    borderRadius: 'var(--radius-md)',
    padding: '1rem',
    fontSize: '0.9rem',
  },
  submitBtn: {
    width: '100%',
    justifyContent: 'center',
    padding: '0.8rem',
    fontSize: '1rem',
    gap: '0.5rem',
  },
  forgotBtn: {
    background: 'none',
    border: 'none',
    color: 'var(--terracotta-dark)',
    fontSize: '0.9rem',
    cursor: 'pointer',
    display: 'block',
    margin: '0.75rem auto 0',
    padding: '0.25rem',
  },
  footer: {
    textAlign: 'center',
    marginTop: '1.25rem',
    fontSize: '0.8rem',
    color: 'var(--navy)',
    opacity: 0.6,
  },
};
